import React, { useState } from 'react';
import sampleData from './data.json';

export type TreeNode = {
	key: string;
	children?: TreeNode[];
	data?: any;
	childCount?: number;
	totalQuestionCount?: number;
	totalItemCount?: number;
};

export const buildTree = (): TreeNode => {
	let totalDataCount = 0;
	let totalItemCount = 0;
	const root: TreeNode = { key: 'Total_Data', children: [], totalQuestionCount: 0, totalItemCount: 0, childCount: 0 };
	const records = sampleData['Total_Data'] as any[];
	const parentMap = new Map<string, TreeNode>();
	const parentCounts = new Map<string, { totalQuestions: number; totalItems: number }>();

	records.forEach((record) => {
		const parentKey = record.parent_intent || 'Unknown Parent';
		const childKey = record.child_intent || 'Unknown Child';

		if (!parentMap.has(parentKey)) {
			parentMap.set(parentKey, {
				key: parentKey,
				children: [],
				totalQuestionCount: 0,
				totalItemCount: 0,
				childCount: 0,
			});
			parentCounts.set(parentKey, { totalQuestions: 0, totalItems: 0 });
		}

		const parentNode = parentMap.get(parentKey)!;
		const { no_api, static_q, lookup_q, transaction_q } = record;

		const sumCounts = (field: any, countKey: string) =>
			Array.isArray(field) ? field.reduce((acc, item) => acc + (item[countKey] || 0), 0) : 0;

		const noApiCount = sumCounts(no_api, 'no_api_cnt');
		const staticQCount = sumCounts(static_q, 'static_q_cnt');
		const lookupQCount = sumCounts(lookup_q, 'lookup_q_cnt');
		const transactionalCount = sumCounts(transaction_q, 'transactional_api_cnt');

		const totalQuestionCount = noApiCount + staticQCount + lookupQCount + transactionalCount;
		const totalItems =
			(no_api?.length || 0) + (static_q?.length || 0) + (lookup_q?.length || 0) + (transaction_q?.length || 0);

		const formattedChildKey = `${childKey} (${totalItems})`;

		const structuredData: any = {};
		if (no_api) structuredData[`no_api (${no_api.length})`] = no_api;
		if (static_q) structuredData[`static_q (${static_q.length})`] = static_q;
		if (lookup_q) structuredData[`lookup_q (${lookup_q.length})`] = lookup_q;
		if (transaction_q) structuredData[`transaction_q (${transaction_q.length})`] = transaction_q;

		const childNode: TreeNode = {
			key: formattedChildKey,
			data: structuredData,
			totalQuestionCount,
			totalItemCount: totalItems,
			childCount: 0,
		};

		parentNode.children!.push(childNode);
		parentCounts.get(parentKey)!.totalQuestions += totalQuestionCount;
		parentCounts.get(parentKey)!.totalItems += totalItems;
	});

	parentMap.forEach((node, key) => {
		const totalParentQuestions = parentCounts.get(key)?.totalQuestions || 0;
		const totalParentItems = parentCounts.get(key)?.totalItems || 0;
		totalDataCount += totalParentQuestions;
		totalItemCount += totalParentItems;

		node.key = `${key} (${totalParentItems})`;
		node.childCount = node.children ? node.children.length : 0;
	});

	root.key = `Total_Data (${totalItemCount})`;
	root.totalQuestionCount = totalDataCount;
	root.totalItemCount = totalItemCount;
	root.children = Array.from(parentMap.values());

	return root;
};

const TreeNodeComponent: React.FC<{ node: TreeNode }> = ({ node }) => {
	const [expanded, setExpanded] = useState(false);
	const [expandedFields, setExpandedFields] = useState<Record<string, boolean>>({});

	const hasChildren = node.children && node.children.length > 0;
	const hasData = node.data && Object.keys(node.data).length > 0;
	const canExpand = hasChildren || hasData;
	const toggleIcon = canExpand ? (expanded ? '-' : '+') : '•';

	const toggleField = (field: string) => {
		setExpandedFields((prev) => ({
			...prev,
			[field]: !prev[field],
		}));
	};

	return (
		<div className="ml-5 mb-2">
			<div
				className="flex items-center space-x-2 cursor-pointer font-semibold text-gray-800"
				onClick={() => canExpand && setExpanded(!expanded)}
			>
				<span>{toggleIcon}</span>
				<span>{node.key}</span>
			</div>

			{expanded && hasChildren && (
				<div className="border-l border-dashed border-gray-400 pl-4 ml-1 mt-1">
					{node.children!.map((child) => (
						<TreeNodeComponent key={child.key} node={child} />
					))}
				</div>
			)}

			{expanded && hasData && (
				<div className="ml-6 mt-2">
					{Object.entries(node.data).map(([k, v]) => (
						<div key={k} className="mt-2">
							<div className="cursor-pointer text-gray-800 font-medium" onClick={() => toggleField(k)}>
								<span>{expandedFields[k] ? '-' : '+'}</span> {k}
							</div>

							{expandedFields[k] && Array.isArray(v) && (
								<table className="w-full mt-2 border border-gray-300">
									<thead>
										<tr className="bg-gray-200 text-sm">
											{Object.keys(v[0] || {}).map((field) => (
												<th key={field} className="px-2 py-1.5 text-left bg-gray-100 font-semibold text-gray-700">
													{field}
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{v.map((entry: any, index: number) => (
											<tr key={index} className="border-b border-gray-300">
												{Object.values(entry).map((value, idx) => (
													<td key={idx} className="px-2 py-1.5 text-xs bg-white">
														{String(value)}
													</td>
												))}
											</tr>
										))}
									</tbody>
								</table>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export const flattenTree = (node: TreeNode, path: string[] = []): any[] => {
	const currentPath = [...path, node.key];
	let rows: any[] = [];

	if (node.data) {
		Object.entries(node.data).forEach(([dataType, entries]) => {
			if (Array.isArray(entries)) {
				entries.forEach((entry: any) => {
					rows.push({
						path: currentPath.join(' > '),
						dataType,
						...entry,
					});
				});
			}
		});
	} else {
		rows.push({
			path: currentPath.join(' > '),
			key: node.key,
		});
	}

	if (node.children) {
		node.children.forEach((child) => {
			rows = rows.concat(flattenTree(child, currentPath));
		});
	}
	return rows;
};

export const convertToCSV = (rows: any[]): string => {
	if (!rows.length) return '';
	const headersSet = new Set<string>();
	rows.forEach((row) => {
		Object.keys(row).forEach((key) => headersSet.add(key));
	});
	const headers = Array.from(headersSet);
	const csvRows = [];
	csvRows.push(headers.join(','));
	rows.forEach((row) => {
		const values = headers.map((header) => `"${String(row[header] || '').replace(/"/g, '""')}"`);
		csvRows.push(values.join(','));
	});
	return csvRows.join('\n');
};

const JsonTreeDisplay: React.FC = () => {
	const treeData = buildTree();

	return (
		<div className="p-4 h-[600px] overflow-auto">
			<TreeNodeComponent node={treeData} />
		</div>
	);
};

export default JsonTreeDisplay;
